import { z } from "zod";
const IsoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "expected YYYY-MM-DD");
const NullableString = z.string().nullable();
/**
 * A dated ECC public archive available to the Margin Lab. `risk_available`
 * means the immutable raw file can be parsed on demand for its PC-SPAN futures
 * arrays; it does not imply every initial-margin add-on has been calculated.
 */
export const EexMarginLabRiskSnapshotSchema = z.object({
    id: z.string().uuid(),
    businessDate: IsoDate,
    fileName: z.string().min(1),
    spanCreatedAt: NullableString,
    archivedAt: z.string().datetime({ offset: true }),
    importedAt: z.string().datetime({ offset: true }).nullable(),
    settlementCount: z.number().int().nonnegative().nullable(),
    availability: z.enum(["risk_available", "archive_pending", "import_failed"]),
    availabilityReason: z.string().min(1),
}).strict();
export const EexMarginLabCandidatePositionSchema = z.object({
    id: z.string().uuid(),
    tradeId: z.string().min(1),
    tradedAt: NullableString,
    product: z.string().min(1),
    period: z.string().min(1),
    side: z.enum(["B", "S"]),
    signedQty: z.number().int(),
    book: NullableString,
    trader: z.object({
        label: z.string().min(1),
        identityMasked: z.boolean(),
    }).strict(),
    origin: z.enum(["cleared", "bilateral"]),
    eligible: z.boolean(),
    exclusionReason: NullableString,
}).strict();
export const EexMarginLabBootstrapResponseSchema = z.object({
    positions: z.array(EexMarginLabCandidatePositionSchema),
    riskSnapshots: z.array(EexMarginLabRiskSnapshotSchema),
    defaultRiskSnapshotId: z.string().uuid().nullable(),
    estimator: z.object({
        status: z.enum(["available", "unavailable"]),
        reason: z.string().min(1),
    }).strict(),
    marginAccountStatus: z.object({
        available: z.literal(false),
        reason: z.string().min(1),
    }).strict(),
}).strict();
export const EexMarginLabCalculationRequestSchema = z.object({
    riskSnapshotId: z.string().uuid(),
    selectedPositionIds: z.array(z.string().uuid()).max(5_000),
    simulatedPositions: z.array(z.object({
        product: z.string().trim().min(1).max(120),
        period: z.string().trim().min(1).max(60),
        signedQty: z.number().int().min(-2_147_483_647).max(2_147_483_647).refine((value) => value !== 0),
        book: z.string().trim().min(1).max(120).nullable().optional(),
        trader: z.string().trim().min(1).max(120).optional(),
    }).strict()).max(1_000),
}).strict();
const EexMarginLabScopeScanRiskSchema = z.object({
    label: z.string().min(1),
    status: z.enum(["calculated", "unavailable"]),
    currency: z.string().length(3).nullable(),
    scanRisk: z.number().nonnegative().nullable(),
    isSpanComponentComplete: z.boolean(),
    completenessReason: z.string().min(1),
}).strict();
export const EexMarginLabCalculationResponseSchema = z.object({
    riskSnapshotId: z.string().uuid(),
    businessDate: IsoDate,
    status: z.enum(["calculated", "unavailable"]),
    method: z.literal("pc_span_futures_scan_risk"),
    currency: z.string().length(3).nullable(),
    scanRisk: z.number().nonnegative().nullable(),
    isSpanComponentComplete: z.boolean(),
    completenessReason: z.string().min(1),
    combinedCommodities: z.array(z.object({
        productCode: z.string().min(1),
        currency: z.string().length(3),
        riskRate: z.number().int().positive(),
        worstScenario: z.number().int().min(1).max(16),
        scanRisk: z.number().nonnegative(),
        scenarioPnl: z.array(z.number()).length(16),
    }).strict()),
    contracts: z.array(z.object({
        productCode: z.string().min(1),
        periodCode: z.string().regex(/^\d{6}$/),
        currency: z.string().length(3),
        signedLots: z.number().int(),
        riskRate: z.number().int().positive(),
        worstScenario: z.number().int().min(1).max(16),
        worstScenarioPnl: z.number(),
    }).strict()),
    exclusions: z.array(z.object({
        productCode: z.string().min(1),
        periodCode: z.string().regex(/^\d{6}$/),
        signedLots: z.number().int(),
        reason: z.string().min(1),
    }).strict()),
    breakdowns: z.object({
        byBook: z.array(EexMarginLabScopeScanRiskSchema),
        byTrader: z.array(EexMarginLabScopeScanRiskSchema),
    }).strict(),
}).strict();
//# sourceMappingURL=eex-margin-lab-contracts.js.map